import { useState } from 'react';
import { Building2, ChevronDown, Check, Globe, Lock, Crown } from 'lucide-react';
import { useBranch } from '../context/BranchContext';

export function BranchSelector() {
  const { branches, currentBranch, switchBranch, loading, hasAllAccess, isHeadquarters } = useBranch();
  const [isOpen, setIsOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-stone-100 rounded-lg animate-pulse">
        <Building2 className="w-4 h-4 text-stone-400" />
        <span className="text-sm text-stone-400">Loading...</span>
      </div>
    );
  }

  if (branches.length === 0) {
    return null;
  }

  const isCurrentHQ = currentBranch && isHeadquarters(currentBranch.id);

  return (
    <div className="relative" data-testid="branch-selector">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors dark:bg-stone-800 dark:hover:bg-stone-700 w-full"
      >
        {currentBranch ? (
          isCurrentHQ ? (
            <Crown className="w-4 h-4 text-amber-500" />
          ) : (
            <Building2 className="w-4 h-4 text-[#4A7C59]" />
          )
        ) : (
          <Globe className="w-4 h-4 text-[#4A7C59]" />
        )}
        <span className="text-sm font-medium text-stone-700 dark:text-stone-200 max-w-[120px] truncate flex-1 text-left">
          {currentBranch ? currentBranch.name : 'All Branches'}
        </span>
        {isCurrentHQ && (
          <span className="text-[10px] font-bold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">HQ</span>
        )}
        {!hasAllAccess && (
          <Lock className="w-3 h-3 text-amber-500" title="Branch access restricted" />
        )}
        <ChevronDown className={`w-4 h-4 text-stone-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)} 
          />
          
          {/* Dropdown */}
          <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-stone-200 overflow-hidden z-50 dark:bg-stone-800 dark:border-stone-700">
            <div className="p-2 border-b border-stone-100 dark:border-stone-700">
              <p className="text-xs font-medium text-stone-400 uppercase tracking-wider px-2">
                {hasAllAccess ? 'Select Branch' : 'Your Assigned Branches'}
              </p>
            </div>
            
            {/* Restricted access notice */}
            {!hasAllAccess && (
              <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 dark:bg-amber-900/20 dark:border-amber-800">
                <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Access limited to assigned branches
                </p>
              </div>
            )}
            
            <div className="max-h-64 overflow-y-auto py-1">
              {/* All Branches option - only for users with full access */}
              {hasAllAccess && (
                <>
                  <button
                    onClick={() => {
                      switchBranch(null);
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors ${
                      !currentBranch ? 'bg-[#4A7C59]/10' : ''
                    }`}
                    data-testid="branch-option-all"
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      !currentBranch ? 'bg-[#4A7C59] text-white' : 'bg-stone-100 text-stone-500 dark:bg-stone-700'
                    }`}>
                      <Globe className="w-4 h-4" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className={`text-sm font-medium ${!currentBranch ? 'text-[#4A7C59]' : 'text-stone-700 dark:text-stone-200'}`}>
                        All Branches
                      </p>
                      <p className="text-xs text-stone-500">View data from all locations</p>
                    </div>
                    {!currentBranch && (
                      <Check className="w-4 h-4 text-[#4A7C59]" />
                    )}
                  </button>

                  {/* Divider */}
                  <div className="h-px bg-stone-100 dark:bg-stone-700 my-1" />
                </>
              )}

              {/* Branch list */}
              {branches.filter(b => b.is_active !== false).map((branch) => {
                const branchIsHQ = isHeadquarters(branch.id);
                return (
                  <button
                    key={branch.id}
                    onClick={() => {
                      switchBranch(branch);
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors ${
                      currentBranch?.id === branch.id ? 'bg-[#4A7C59]/10' : ''
                    }`}
                    data-testid={`branch-option-${branch.id}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      currentBranch?.id === branch.id 
                        ? branchIsHQ ? 'bg-amber-500 text-white' : 'bg-[#4A7C59] text-white' 
                        : 'bg-stone-100 text-stone-500 dark:bg-stone-700'
                    }`}>
                      {branchIsHQ ? <Crown className="w-4 h-4" /> : <Building2 className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-medium truncate ${
                          currentBranch?.id === branch.id ? 'text-[#4A7C59]' : 'text-stone-700 dark:text-stone-200'
                        }`}>
                          {branch.name}
                        </p>
                        {branchIsHQ && (
                          <span className="text-[9px] font-bold text-amber-600 bg-amber-100 px-1 py-0.5 rounded flex-shrink-0">
                            HQ
                          </span>
                        )}
                      </div>
                      {branch.address && (
                        <p className="text-xs text-stone-500 truncate">{branch.address}</p>
                      )}
                    </div>
                    {currentBranch?.id === branch.id && (
                      <Check className="w-4 h-4 text-[#4A7C59] flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default BranchSelector;
